import { isCompletedGame } from '@/app/[locale]/match/libs/retirement';

export interface ScoreLineGame {
  player1: number;
  player2: number;
  status?: string | null;
}

/**
 * The score line of a match: every game actually played, "11-5, 3-11". A retired match ends with
 * its marker ("10-12, 1-0 ret."): the unfinished game is shown as played, never dropped.
 */
export function formatScoreLine(
  games: ReadonlyArray<ScoreLineGame>,
  opts: { retired: boolean; marker: string },
): string {
  const line = games.map((g) => `${g.player1}-${g.player2}`).join(', ');
  return opts.retired && line ? `${line} ${opts.marker}` : line;
}

/** A stored `match_game` row, as the admin schedule cards read it. */
export interface StoredGameRow {
  game_number: number;
  player_1_score: number;
  player_2_score: number;
  status?: string | null;
}

/**
 * The two lines an admin schedule card shows for a finished match: games won (completed rows
 * only) and the points line (every game with a point, ending in the marker for a retirement).
 */
export function summariseStoredGames(
  rows: ReadonlyArray<StoredGameRow>,
  opts: { retired: boolean; marker: string },
): { gameScore: string; gamePoints: string | null } {
  const games = [...rows]
    .sort((a, b) => a.game_number - b.game_number)
    .map((g) => ({ player1: g.player_1_score, player2: g.player_2_score, status: g.status }));
  const won = completedGamesWon(games);
  const played = games.filter((g) => g.player1 > 0 || g.player2 > 0);
  return {
    gameScore: `${won.player1}-${won.player2}`,
    gamePoints: played.length > 0 ? formatScoreLine(played, opts) : null,
  };
}

/**
 * Games each side won, counting completed games only. A retired match's unfinished game
 * (`status 'in_progress'`) never counts, and the standings credit is not a games total.
 */
export function completedGamesWon(games: ReadonlyArray<ScoreLineGame>): {
  player1: number;
  player2: number;
} {
  const won = { player1: 0, player2: 0 };
  for (const g of games) {
    if (!isCompletedGame(g)) continue;
    if (g.player1 > g.player2) won.player1++;
    else if (g.player2 > g.player1) won.player2++;
  }
  return won;
}
